
import { supabase } from './client';

export interface InventoryLog {
  id: string;
  product_id: string;
  organization_id: string;
  user_id?: string;
  quantity_change: number;
  previous_quantity?: number;
  new_quantity: number;
  reason?: string;
  notes?: string;
  created_at: string;
  user_name?: string; // Joined from auth.users if available
}

export interface StockAdjustmentParams {
  productId: string; // Using string for UUID
  quantityChange: number; 
  reason?: string;
  notes?: string;
}

/**
 * Fetch inventory logs for a specific product
 */
export const getInventoryLogs = async (productId: string): Promise<InventoryLog[]> => {
  try {
    const { data, error } = await supabase
      .from('inventory_logs')
      .select('*')
      .eq('product_id', productId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching inventory logs:', error);
    throw error;
  }
};

/**
 * Adjust product stock and create a log entry
 */
export const adjustProductStock = async (
  params: StockAdjustmentParams
): Promise<{ success: boolean; newQuantity?: number; error?: string }> => {
  try {
    const productIdStr = params.productId;
    
    console.log('Adjusting stock for product:', productIdStr, 'with change:', params.quantityChange);
    
    // Call the RPC function to update inventory
    const { data, error } = await supabase.rpc(
      'update_product_inventory',
      {
        p_product_id: productIdStr,
        p_quantity_change: params.quantityChange,
        p_reason: params.reason || null,
        p_notes: params.notes || null
      }
    );
    
    if (error) {
      console.error('RPC error:', error);
      throw error;
    }
    
    console.log('Stock adjustment result:', data);
    
    if (!data || !data.success) {
      // Fallback: if RPC function fails or hasn't been updated, update directly
      console.log('Fallback: updating stock directly');
      
      // Get current product
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('stock_quantity')
        .eq('id', productIdStr)
        .single();
      
      if (productError) throw productError;
      
      const currentQuantity = product.stock_quantity || 0;
      const newQuantity = currentQuantity + params.quantityChange;
      
      // Update stock
      const { error: updateError } = await supabase
        .from('products')
        .update({ stock_quantity: newQuantity })
        .eq('id', productIdStr);
      
      if (updateError) throw updateError;
      
      // Create manual log
      const { error: logError } = await supabase
        .from('inventory_logs')
        .insert({
          product_id: productIdStr,
          quantity_change: params.quantityChange,
          previous_quantity: currentQuantity,
          new_quantity: newQuantity,
          reason: params.reason,
          notes: params.notes
        });
      
      if (logError) {
        console.warn('Failed to create inventory log:', logError);
        // Continue even if log creation fails
      }
      
      return { 
        success: true, 
        newQuantity: newQuantity 
      };
    }
    
    return { 
      success: true, 
      newQuantity: data.new_quantity 
    };
  } catch (error) {
    console.error('Error adjusting product stock:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Get recent inventory activity across all products
 */
export const getRecentInventoryActivity = async (limit = 10): Promise<InventoryLog[]> => {
  try {
    const { data, error } = await supabase
      .from('inventory_logs')
      .select(`
        *,
        products:product_id (name, sku)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching recent inventory activity:', error);
    throw error;
  }
};
